import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  Button,
  Drawer,
  Dropdown,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Space,
  Spin,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { navigationGroups } from '@/components/admin/navigation';
import AdminCommandMenu from '@/components/admin/AdminCommandMenu';
import AdminPageBoundary from '@/components/admin/AdminPageBoundary';
import { useAuthStore } from '@/stores/auth.store';
import request from '@/utils/request';

const { Header, Sider, Content } = Layout;

type ChangePasswordFormValues = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const menuItems: MenuProps['items'] = navigationGroups.map((group) => ({
  type: 'group',
  key: group.key,
  label: group.label,
  children: group.items.map(({ key, label, icon }) => ({ key, label, icon })),
}));

const allNavigationItems = navigationGroups.flatMap((group) =>
  group.items.map((item) => ({ ...item, groupLabel: group.label })),
);

function resolveNavigation(pathname: string) {
  if (pathname.startsWith('/food-reports/')) {
    return {
      activeKey: '/foods',
      groupLabel: '内容与审核',
      pageTitle: '食品报错详情',
    };
  }
  if (pathname.startsWith('/food-submissions/')) {
    return {
      activeKey: '/foods',
      groupLabel: '内容与审核',
      pageTitle: '食物补报详情',
    };
  }
  const match = [...allNavigationItems]
    .sort((a, b) => b.key.length - a.key.length)
    .find((item) => pathname === item.key || pathname.startsWith(`${item.key}/`));
  const item = match ?? allNavigationItems[0];
  const isDetail = pathname !== item.key;
  return {
    activeKey: item.key,
    groupLabel: item.groupLabel,
    pageTitle: isDetail ? (item.detailLabel ?? `${item.label}详情`) : item.label,
  };
}

function Brand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className={`purrcare-brand${collapsed ? ' purrcare-brand--collapsed' : ''}`}>
      <span className="purrcare-brand-mark" aria-hidden="true">
        🐾
      </span>
      {!collapsed && (
        <div className="purrcare-brand-copy">
          <div className="purrcare-brand-name">猫知岁</div>
          <div className="purrcare-brand-subtitle">MeowMoir Admin</div>
        </div>
      )}
    </div>
  );
}

function NavigationMenu({
  activeKey,
  onNavigate,
}: {
  activeKey: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <nav
      className="purrcare-nav"
      aria-label="管理员功能导航"
      style={{ flex: '1 1 0%', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}
    >
      <Menu
        mode="inline"
        selectedKeys={[activeKey]}
        items={menuItems}
        onClick={({ key }) => onNavigate(key)}
      />
    </nav>
  );
}

const AuthLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, admin, logout, checkSession, loading } = useAuthStore();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('admin_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm<ChangePasswordFormValues>();

  useEffect(() => {
    try {
      localStorage.setItem('admin_sidebar_collapsed', String(collapsed));
    } catch {
      /* Optional preference. */
    }
  }, [collapsed]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const pageContext = useMemo(() => resolveNavigation(location.pathname), [location.pathname]);
  useEffect(() => {
    document.title = `${pageContext.pageTitle} · 猫知岁管理工作台`;
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pageContext.pageTitle, location.pathname]);

  useEffect(() => {
    checkSession().finally(() => setSessionChecked(true));
    let lastCheckAt = Date.now();
    const renewSession = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastCheckAt < 60_000) return;
      lastCheckAt = Date.now();
      void checkSession();
    };
    const timer = window.setInterval(renewSession, 60 * 60 * 1000);
    window.addEventListener('focus', renewSession);
    window.addEventListener('online', renewSession);
    document.addEventListener('visibilitychange', renewSession);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', renewSession);
      window.removeEventListener('online', renewSession);
      document.removeEventListener('visibilitychange', renewSession);
    };
  }, [checkSession]);

  useEffect(() => {
    if (sessionChecked && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [sessionChecked, isAuthenticated, navigate]);

  const handleNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleChangePassword = async ({ oldPassword, newPassword }: ChangePasswordFormValues) => {
    setPasswordLoading(true);
    try {
      await request.post('/admin/change-password', { oldPassword, newPassword });
      message.success('密码已更新，请使用新密码登录');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
      handleLogout();
    } catch (err) {
      message.error((err as Error)?.message || '密码更新失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: 'password', icon: <KeyOutlined />, label: '修改密码' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  const onUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'password') setPasswordModalOpen(true);
    if (key === 'logout') handleLogout();
  };

  if (!sessionChecked || loading) {
    return (
      <div className="purrcare-auth-loading" aria-label="正在验证管理员身份">
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout className="purrcare-app">
      <a className="admin-skip-link" href="#admin-main-content">
        跳到主要内容
      </a>
      <AdminCommandMenu open={commandOpen} onClose={() => setCommandOpen(false)} />
      <Sider
        width={216}
        collapsedWidth={80}
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="light"
        className="purrcare-desktop-sider"
      >
        <div className="purrcare-sider-inner">
          <Brand collapsed={collapsed} />
          <NavigationMenu activeKey={pageContext.activeKey} onNavigate={handleNavigate} />
        </div>
      </Sider>

      <Drawer
        className="purrcare-mobile-drawer"
        placement="left"
        width={286}
        open={mobileMenuOpen}
        closable={false}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Brand />
        <NavigationMenu activeKey={pageContext.activeKey} onNavigate={handleNavigate} />
      </Drawer>

      <Layout
        className={`purrcare-shell-main${collapsed ? ' purrcare-shell-main--collapsed' : ''}`}
      >
        <Header className="purrcare-topbar">
          <div className="purrcare-topbar-leading">
            <Button
              type="text"
              className="purrcare-desktop-collapse-button"
              aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((value) => !value)}
            />
            <Button
              type="text"
              className="purrcare-mobile-menu-button"
              aria-label="打开导航菜单"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
            />
            <div className="purrcare-page-context">
              <h1 className="purrcare-page-context-title">{pageContext.pageTitle}</h1>
              <Breadcrumb
                items={[
                  { title: '猫知岁' },
                  { title: pageContext.groupLabel },
                  { title: pageContext.pageTitle },
                ]}
              />
            </div>
          </div>

          <div className="admin-topbar-actions">
            <Button
              className="admin-command-trigger"
              icon={<SearchOutlined />}
              onClick={() => setCommandOpen(true)}
              aria-label="搜索全部管理功能"
            >
              <span>搜索功能</span>
              <kbd>⌘ K</kbd>
            </Button>
            <Dropdown
              menu={{ items: userMenuItems, onClick: onUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                className="purrcare-user-button"
                icon={<UserOutlined />}
                aria-label={`管理员菜单 ${admin?.username ?? '管理员'}`}
              >
                {admin?.username ?? '管理员'}
              </Button>
            </Dropdown>
          </div>
        </Header>

        <Content className="purrcare-content" id="admin-main-content">
          <AdminPageBoundary key={location.pathname}>
            <Suspense
              fallback={
                <div className="purrcare-chart-state" aria-label="正在加载管理页面">
                  <Spin />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </AdminPageBoundary>
        </Content>
      </Layout>

      <Modal
        title="修改密码"
        open={passwordModalOpen}
        onCancel={() => {
          setPasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少需要 8 个字符' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setPasswordModalOpen(false);
                  passwordForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={passwordLoading}>
                确认修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AuthLayout;
